// Where each note field surfaces on the INVESTOR dashboard — read off the Individual Note fact sheet in
// investor-dashboard-next (`pages/Public/IndividualNotes/Detail`: Resume + Loan Details + Property Details
// + Additional Info + Docs & Updates, 2026-09-09). ONE table, read by the Add Property wizard (one step
// per section, the section's real screenshot beside the fields) and by the note detail, which take the
// LABEL of every box from here. Since 2026-09-10 (TL) the form only asks what somebody reads: a column
// nobody reads on the new system left the form (the data stays in the base, an update without it does
// not touch it), and the two operator actions (availability, loan status) are switches in the note's
// header, not boxes. Since 2026-09-12 there is no exception left: `appraisal` / `inspectionReports`
// were the last columns asked for that nothing paints — the dashboard replaced them with the real
// document collection on 2026-09-05 — so the panel no longer offers them either.

export const DASHBOARD_SECTIONS = {
  summary: 'Summary',
  loan: 'Loan Details',
  property: 'Property Details',
  additional: 'Additional Info',
  docs: 'Docs & Updates',
};

// THE BOX IS NAMED BY THE INVESTOR'S WORD (TL, 2026-09-10). Until today our functional name was the label
// and the investor's word a muted suffix, and the operator kept mixing up which amount was which; it is
// now the other way round — the dashboard's word IS the label, and under it one line says what the column
// does.
//
// `label`: the dashboard's own words, verbatim and in the case it paints them (the stat cards shout).
// `does`:  the line UNDER the label. A column nothing but a screen reads says so — "Informational" is an
//          answer, and a guess is not one: no line here claims an effect that is not in the backend.
// `element: true`: the dashboard paints this piece with NO label of its own (the photo, the tag line, the
//          three halves of the title), so there is no investor word to promote — the box keeps its
//          functional name and the line says where the piece lands.
export const DASHBOARD_MAP = {
  image: {
    section: 'summary',
    label: 'hero image',
    element: true,
    does: 'Informational — the photo at the top of the note.',
  },
  loanTag: {
    section: 'summary',
    label: 'tag line',
    element: true,
    does: "Informational — printed over the card as the note's tag line.",
  },
  address: {
    section: 'summary',
    label: 'title',
    element: true,
    does: 'The title investors read, and the name this note carries in their holdings.',
  },
  city: { section: 'summary', label: 'title', element: true, does: 'Informational — part of the title, under the address.' },
  state: { section: 'summary', label: 'title', element: true, does: 'Informational — part of the title, under the address.' },
  zipCode: { section: 'summary', label: 'title', element: true, does: 'Informational — part of the title, under the address.' },
  dealOverview: {
    section: 'summary',
    label: 'Why we originated this loan',
    does: 'Informational — the paragraph investors read under the header.',
  },
  netYield: {
    section: 'summary',
    label: 'NET YIELD',
    does: 'Prices the position: accrued interest and next monthly interest are estimated from it.',
  },
  maturityDate: {
    section: 'summary',
    label: 'MATURITY DATE',
    does: 'Shown to investors, and their holdings are ordered by it — soonest to mature first.',
  },
  fundingAmount: {
    section: 'summary',
    label: 'TOTAL LOAN AMOUNT',
    does: 'The real cap — rails allocate against this and partial fills are trimmed to it.',
  },

  investorYieldMaturity: { section: 'loan', label: 'YIELD', does: 'Informational — shown to investors.' },
  termLength: { section: 'loan', label: 'REMAINING TERM', does: 'Informational — shown to investors.' },
  investmentAmount: {
    section: 'loan',
    label: 'INITIAL LOAN AMOUNT',
    does: 'Informational — the loan made to the borrower. It does not affect the cap or any calculation.',
  },
  rehabBudget: { section: 'loan', label: 'CONSTRUCTION RESERVE', does: 'Informational — shown to investors.' },
  arv: { section: 'loan', label: 'ARV', does: 'Informational — shown to investors.' },
  asIsValuePurchasePrice: { section: 'loan', label: 'PURCHASE PRICE', does: 'Informational — shown to investors.' },
  asIsLtv: { section: 'loan', label: 'AS-IS LTV', does: 'Informational — shown to investors.' },
  arvLtv: { section: 'loan', label: 'ARV LTV', does: 'Informational — shown to investors.' },

  propertyTag: { section: 'property', label: 'PROPERTY TYPE', does: 'Informational — shown to investors.' },
  numberOfUnits: { section: 'property', label: 'NUMBER OF UNITS', does: 'Informational — shown to investors.' },

  loanPurpose: { section: 'additional', label: 'LOAN PURPOSE', does: 'Informational — shown to investors.' },
  originalLoanTerm: { section: 'additional', label: 'ORIGINAL LOAN TERM', does: 'Informational — shown to investors.' },
  notePosition: { section: 'additional', label: 'NOTE POSITION', does: 'Informational — shown to investors.' },
  repaymentType: {
    section: 'additional',
    label: 'REPAYMENT TYPE',
    does: 'Informational — shown to investors (the backend refuses a word outside the list).',
  },
  borrowerCreditScore: { section: 'additional', label: 'CREDIT SCORE', does: 'Informational — shown to investors.' },
  borrowerTrackRecord: { section: 'additional', label: 'TRACK RECORD', does: 'Informational — shown to investors.' },

  documents: {
    section: 'docs',
    label: 'Documents',
    element: true,
    does: "Shown to investors — the note's paperwork, the files they can open.",
  },
};

// What a box shows on a surface that HAS an investor fact sheet: the dashboard's own word as the label
// (undefined where the dashboard paints the piece without one, so the box keeps its functional name), and
// the line that goes under it. The reduced set never calls this — no fact sheet, no investor word.
export function dashboardBox(name) {
  const entry = DASHBOARD_MAP[name];
  if (!entry) return undefined;
  return { label: entry.element ? undefined : entry.label, hint: entry.does };
}
